class MainSearch extends SearchForm {
  constructor() {
    super();
    this.allSearchInputs = document.querySelectorAll('input[type="search"]');
    this.searchProvider = this.dataset.searchProvider || 'shopify';
    this.productGrid = this.closest('section')?.querySelector('#product-grid');
    this.searchAbortController = null;
    this.requestIndex = 0;
    this.handlePopState = this.handlePopState.bind(this);

    if (this.productGrid?.dataset.id) {
      window.searchResultId = this.productGrid.dataset.id;
    }

    this.setupSyncEventListeners();

    if (this.searchProvider === 'luigisbox' && this.dataset.luigisboxTrackerId) {
      this.setupLuigisboxSearchPage();
    }
  }

  setupSyncEventListeners() {
    const allSearchForms = Array.from(new Set(Array.from(this.allSearchInputs).map((input) => input.form).filter(Boolean)));

    this.input.addEventListener('focus', this.onInputFocus.bind(this));

    if (allSearchForms.length < 2) return;

    allSearchForms.forEach((form) => form.addEventListener('reset', this.onFormReset.bind(this)));
    this.allSearchInputs.forEach((input) => input.addEventListener('input', this.onInput.bind(this)));
  }

  setupLuigisboxSearchPage() {
    this.luigisboxApiUrl = this.dataset.luigisboxApiUrl || 'https://live.luigisbox.com/search';
    this.luigisboxTrackerId = this.dataset.luigisboxTrackerId;
    this.luigisboxHostname = this.dataset.luigisboxHostname || window.location.hostname;
    this.luigisboxProvidedCustomerId = this.dataset.luigisboxCustomerId || this.dataset.luigisboxUserId || null;
    this.luigisboxSectionId = this.dataset.luigisboxSectionId || 'quick-order-list';
    this.luigisboxPageSize = Math.max(parseInt(this.dataset.luigisboxPageSize || '24', 10), 1);
    this.luigisboxFacets = (this.dataset.luigisboxFacets || '')
      .split(',')
      .map((facet) => facet.trim().replace(/\+/g, ' '))
      .filter(Boolean);
    this.luigisboxEnableFiltering = this.dataset.luigisboxEnableFiltering === 'true';
    this.currencyCode = this.dataset.luigisboxCurrencyCode || 'CZK';
    this.emptyPromptText = this.dataset.luigisboxEmptyPrompt || 'Začněte hledat produkty.';
    this.emptyResultsText = this.dataset.luigisboxEmptyResults || 'Nenalezli jsme žádné produkty pro tento dotaz.';
    this.errorText = this.dataset.luigisboxErrorText || 'Vyhledávání je dočasně nedostupné.';
    this.countLabel = this.dataset.luigisboxCountLabel || 'výsledků';

    this.form = this.querySelector('[data-luigisbox-search-form]') || this.querySelector('form');
    this.countElement = this.querySelector('[data-luigisbox-count]');
    this.filtersContainer = this.closest('section')?.querySelector('[data-luigisbox-filters]');
    this.resultsContainer = this.closest('section')?.querySelector('[data-luigisbox-results]');
    this.paginationContainer = this.closest('section')?.querySelector('[data-luigisbox-pagination]');
    this.loadingOverlay = this.closest('section')?.querySelector('[data-luigisbox-loading-overlay]');

    if (!this.form || !this.resultsContainer) return;

    this.form.addEventListener('submit', this.onLuigisboxSubmit.bind(this));
    this.form.addEventListener('reset', this.onLuigisboxReset.bind(this));
    this.filtersContainer?.addEventListener('change', this.onLuigisboxFiltersChange.bind(this));
    this.filtersContainer?.addEventListener('click', this.onLuigisboxFiltersClick.bind(this));
    this.paginationContainer?.addEventListener('click', this.onLuigisboxPaginationClick.bind(this));
    window.addEventListener('popstate', this.handlePopState);

    this.loadLuigisboxStateFromLocation({ replaceState: true });
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.handlePopState);
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.keepInSync('', this.input);
    }
  }

  onInput(event) {
    const target = event.target;
    this.keepInSync(target.value, target);
  }

  onInputFocus() {
    const isSmallScreen = window.innerWidth < 750;
    if (isSmallScreen) {
      this.scrollIntoView({ behavior: 'smooth' });
    }
  }

  keepInSync(value, target) {
    this.allSearchInputs.forEach((input) => {
      if (input !== target) {
        input.value = value;
      }
    });
  }

  handlePopState() {
    if (this.searchProvider !== 'luigisbox') return;
    this.loadLuigisboxStateFromLocation({ replaceState: true });
  }

  onLuigisboxSubmit(event) {
    event.preventDefault();

    const state = this.getStateFromControls();
    state.page = 1;

    this.loadLuigisboxState(state);
  }

  onLuigisboxReset() {
    window.setTimeout(() => {
      this.loadLuigisboxState({
        query: '',
        page: 1,
        filters: {},
      });
    }, 0);
  }

  onLuigisboxFiltersChange(event) {
    const input = event.target;

    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox') return;

    const state = this.getStateFromControls();
    state.page = 1;

    this.loadLuigisboxState(state);
  }

  onLuigisboxFiltersClick(event) {
    const button = event.target.closest('[data-luigisbox-clear-filters], [data-luigisbox-remove-filter]');

    if (!button) return;

    event.preventDefault();

    const state = this.getStateFromLocation();

    if (button.hasAttribute('data-luigisbox-clear-filters')) {
      state.filters = {};
    } else {
      const facetName = button.getAttribute('data-facet-name');
      const facetValues = this.deserializeFilterValues(
        button.getAttribute('data-facet-values') || button.getAttribute('data-facet-value')
      );

      if (facetName && facetValues.length && state.filters[facetName]) {
        state.filters[facetName] = state.filters[facetName].filter((value) => !facetValues.includes(value));

        if (!state.filters[facetName].length) {
          delete state.filters[facetName];
        }
      }
    }

    state.page = 1;
    this.loadLuigisboxState(state);
  }

  onLuigisboxPaginationClick(event) {
    const link = event.target.closest('[data-page]');

    if (!link) return;

    event.preventDefault();

    const nextPage = parseInt(link.getAttribute('data-page'), 10);

    if (!nextPage || nextPage < 1) return;

    const state = this.getStateFromLocation();
    state.page = nextPage;

    this.loadLuigisboxState(state, { scroll: true });
  }

  getStateFromControls() {
    const formData = new FormData(this.form);
    const query = String(formData.get('q') || '').trim();
    const filters = {};

    this.getSelectedFilterValues().forEach((rawFilter) => {
      const filter = String(rawFilter || '');
      const separatorIndex = filter.indexOf(':');

      if (separatorIndex === -1 || filter === 'type:product') return;

      const facetName = filter.slice(0, separatorIndex);
      const facetValue = filter.slice(separatorIndex + 1);

      filters[facetName] = filters[facetName] || [];
      filters[facetName].push(facetValue);
    });

    return {
      query,
      page: 1,
      filters,
    };
  }

  getSelectedFilterValues() {
    if (!this.filtersContainer) {
      return [];
    }

    return Array.from(this.filtersContainer.querySelectorAll('input[name="f[]"]:checked')).flatMap((input) => {
      const facetName = input.getAttribute('data-facet-name');

      if (!facetName) {
        return this.deserializeFilterValues(input.value);
      }

      return this.deserializeFilterValues(input.value).map((value) => `${facetName}:${value}`);
    });
  }

  getStateFromLocation() {
    const url = new URL(window.location.href);
    const params = url.searchParams;
    const query = (params.get('q') || '').trim();
    const page = Math.max(parseInt(params.get('page') || '1', 10) || 1, 1);
    const filters = {};

    params.getAll('f[]').forEach((rawFilter) => {
      const filter = String(rawFilter || '');
      const separatorIndex = filter.indexOf(':');

      if (separatorIndex === -1 || filter === 'type:product') return;

      const facetName = filter.slice(0, separatorIndex);
      const facetValue = filter.slice(separatorIndex + 1);

      filters[facetName] = filters[facetName] || [];

      if (!filters[facetName].includes(facetValue)) {
        filters[facetName].push(facetValue);
      }
    });

    return {
      query,
      page,
      filters,
    };
  }

  loadLuigisboxStateFromLocation(options = {}) {
    this.loadLuigisboxState(this.getStateFromLocation(), { ...options, updateHistory: false });
  }

  loadLuigisboxState(state, options = {}) {
    const normalizedState = this.normalizeState(state);
    const {
      replaceState = false,
      updateHistory = true,
      scroll = false,
    } = options;

    this.input.value = normalizedState.query;
    this.toggleResetButton();
    this.keepInSync(normalizedState.query, this.input);

    if (updateHistory) {
      const nextUrl = this.buildUrlForState(normalizedState);
      const currentUrl = `${window.location.pathname}${window.location.search}`;
      const nextUrlString = `${nextUrl.pathname}${nextUrl.search}`;

      if (nextUrlString !== currentUrl) {
        const method = replaceState ? 'replaceState' : 'pushState';
        window.history[method]({}, '', nextUrlString);
      }
    }

    this.fetchLuigisboxResults(normalizedState, { scroll });
  }

  normalizeState(state) {
    const normalizedState = {
      query: String(state.query || '').trim(),
      page: Math.max(parseInt(state.page || 1, 10) || 1, 1),
      filters: {},
    };

    Object.entries(state.filters || {}).forEach(([facetName, values]) => {
      const uniqueValues = Array.from(new Set((values || []).map((value) => String(value).trim()).filter(Boolean)));

      if (uniqueValues.length) {
        normalizedState.filters[facetName] = uniqueValues;
      }
    });

    return normalizedState;
  }

  buildUrlForState(state) {
    const url = new URL(window.location.href);
    url.search = '';

    if (state.query) {
      url.searchParams.set('q', state.query);
      url.searchParams.set('options[prefix]', 'last');
    }

    Object.entries(state.filters || {}).forEach(([facetName, values]) => {
      values.forEach((value) => {
        url.searchParams.append('f[]', `${facetName}:${value}`);
      });
    });

    if (state.query) {
      url.searchParams.append('f[]', 'type:product');
    }

    if (state.page > 1 && state.query) {
      url.searchParams.set('page', String(state.page));
    }

    return url;
  }

  fetchLuigisboxResults(state, options = {}) {
    const { scroll = false } = options;

    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }

    this.searchAbortController = new AbortController();
    const requestId = ++this.requestIndex;

    if (!state.query) {
      this.renderLuigisboxBlankState();
      return;
    }

    this.setLuigisboxLoadingState(true);

    const requestUrl = new URL(this.luigisboxApiUrl);
    requestUrl.searchParams.set('tracker_id', this.luigisboxTrackerId);
    requestUrl.searchParams.set('q', state.query);
    requestUrl.searchParams.set('size', String(this.luigisboxPageSize));
    requestUrl.searchParams.set('page', String(state.page));
    requestUrl.searchParams.set('hostname', this.luigisboxHostname);
    requestUrl.searchParams.set('hit_fields', 'url');
    this.applyLuigisboxRequestIdentity(requestUrl.searchParams, this.luigisboxProvidedCustomerId);
    requestUrl.searchParams.set('remove_fields', 'nested');
    requestUrl.searchParams.append('f[]', 'type:product');

    Object.entries(state.filters).forEach(([facetName, values]) => {
      values.forEach((value) => requestUrl.searchParams.append('f[]', `${facetName}:${value}`));
    });

    if (this.luigisboxEnableFiltering && this.luigisboxFacets.length) {
      requestUrl.searchParams.set('facets', this.luigisboxFacets.join(','));
    }

    fetch(requestUrl.toString(), {
      signal: this.searchAbortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(String(response.status));
        }

        return response.json();
      })
      .then((data) => this.renderLuigisboxResponse(data, state, requestId, scroll))
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.renderLuigisboxErrorState();
      });
  }

  renderLuigisboxResponse(data, state, requestId, scroll) {
    const results = data?.results || {};
    const hits = results.hits || [];
    const totalHits = results.total_hits || 0;
    const facets = this.luigisboxEnableFiltering ? results.facets || [] : [];

    this.renderLuigisboxCount(totalHits, state.query);
    this.renderLuigisboxFilters(facets, state);

    return this.fetchLuigisboxProductCards(hits, requestId)
      .then((productCards) => {
        if (requestId !== this.requestIndex) return;

        this.renderLuigisboxResults(hits, productCards, state.query);
        this.renderLuigisboxPagination(totalHits, state);
        this.setLuigisboxLoadingState(false);

        if (scroll) {
          this.scrollLuigisboxResultsIntoView();
        }
      })
      .catch((error) => {
        if (error?.name === 'AbortError') return;
        this.renderLuigisboxErrorState();
      });
  }

  fetchLuigisboxProductCards(hits, requestId) {
    if (!hits.length) {
      return Promise.resolve([]);
    }

    return Promise.all(
      hits.map((hit) => {
        const requestUrl = new URL(hit.url, window.location.origin);
        requestUrl.searchParams.set('section_id', this.luigisboxSectionId);

        return fetch(requestUrl.toString(), {
          signal: this.searchAbortController.signal,
          credentials: 'same-origin',
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(String(response.status));
            }

            return response.text();
          })
          .then((responseText) => {
            if (requestId !== this.requestIndex) {
              throw new DOMException('Request cancelled', 'AbortError');
            }

            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const targetElement = html.querySelector('.quick-order-list-container .card-horizontal');
            const cardMarkup = targetElement ? targetElement.outerHTML : null;

            return {
              url: hit.url,
              cardMarkup,
            };
          })
          .catch((error) => {
            if (error?.name === 'AbortError') {
              throw error;
            }

            return {
              url: hit.url,
              cardMarkup: null,
            };
          });
      })
    );
  }

  renderLuigisboxBlankState() {
    this.renderLuigisboxCount(0, '');
    this.renderLuigisboxFilters([], { filters: {} });
    this.renderLuigisboxPagination(0, { page: 1 });
    this.setLuigisboxLoadingState(false);

    this.resultsContainer.innerHTML = `
      <div class="title-wrapper center luigisbox-search-page__placeholder">
        <h2 class="title title--primary">${this.escapeHtml(this.emptyPromptText)}</h2>
      </div>
    `;
  }

  renderLuigisboxErrorState() {
    this.renderLuigisboxCount(0, this.input.value.trim());
    this.renderLuigisboxPagination(0, { page: 1 });
    this.setLuigisboxLoadingState(false);

    this.resultsContainer.innerHTML = `
      <div class="title-wrapper center luigisbox-search-page__error">
        <h2 class="title title--primary">${this.escapeHtml(this.errorText)}</h2>
      </div>
    `;
  }

  renderLuigisboxCount(totalHits, query) {
    if (!this.countElement) return;

    if (!query) {
      this.countElement.textContent = '';
      return;
    }

    this.countElement.textContent = `${totalHits} ${this.countLabel} pro "${query}"`;
  }

  renderLuigisboxFilters(facets, state) {
    if (!this.filtersContainer) return;

    const facetOpenState = this.getFacetOpenState();
    const activeFiltersMarkup = this.buildActiveFiltersMarkup(facets, state.filters || {});
    const facetsMarkup = facets
      .map((facet, index) => this.buildFacetMarkup(facet, state.filters || {}, index, facetOpenState))
      .join('');

    if (!activeFiltersMarkup && !facetsMarkup) {
      this.filtersContainer.innerHTML = '';
      return;
    }

    this.filtersContainer.innerHTML = `
      <facet-filters-form class="facets facets-vertical">
        <form class="facets__form-vertical" data-luigisbox-filters-form>
          <div class="active-facets active-facets-desktop">
            <div class="active-facets-vertical-filter">
              <h2 class="facets__heading facets__heading--vertical caption-large text-body" id="verticalTitle" tabindex="-1">
                ${this.escapeHtml('Filtrovat:')}
              </h2>
              ${
                activeFiltersMarkup
                  ? `
                    <div class="active-facets__button-wrapper">
                      <button type="button" class="active-facets__button-remove underlined-link" data-luigisbox-clear-filters>
                        <span>${this.escapeHtml('Smazat vše')}</span>
                      </button>
                    </div>
                  `
                  : ''
              }
            </div>
            ${activeFiltersMarkup}
          </div>
          ${facetsMarkup}
        </form>
      </facet-filters-form>
    `;
  }

  buildActiveFiltersMarkup(facets, activeFilters) {
    const renderedChips = [];
    const renderedGroups = new Set();
    const facetsByName = (facets || []).reduce((accumulator, facet) => {
      accumulator[facet.name] = facet;
      return accumulator;
    }, {});

    (facets || []).forEach((facet) => {
      this.getFacetEntries(facet, activeFilters).forEach((entry) => {
        if (!entry.checked) return;

        const groupKey = `${facet.name}:${entry.serializedValue}`;
        renderedGroups.add(groupKey);
        renderedChips.push(this.buildActiveFilterChip(facet.name, entry.label, entry.serializedValue));
      });
    });

    Object.entries(activeFilters).forEach(([facetName, values]) => {
      const facet = facetsByName[facetName];

      if (facet) return;

      values.forEach((value) => {
        const groupKey = `${facetName}:${value}`;

        if (renderedGroups.has(groupKey)) return;

        renderedChips.push(this.buildActiveFilterChip(facetName, this.getFallbackFacetValueLabel(facetName, value), value));
      });
    });

    return renderedChips.join('');
  }

  buildActiveFilterChip(facetName, valueLabel, serializedValue) {
    return `
      <button
        type="button"
        class="active-facets__button active-facets__button--light"
        data-luigisbox-remove-filter
        data-facet-name="${this.escapeHtml(facetName)}"
        data-facet-values="${this.escapeHtml(serializedValue)}"
      >
        <span class="active-facets__button-inner button button--tertiary">
          ${this.escapeHtml(this.getFacetDisplayName(facetName))}: ${this.escapeHtml(valueLabel)}
          ${this.getCloseSmallIconMarkup()}
        </span>
      </button>
    `;
  }

  getFacetEntries(facet, activeFilters) {
    const activeValues = activeFilters[facet.name] || [];
    const values = (facet.values || []).filter((value) => Number(value.hits_count || 0) > 0 || activeValues.includes(String(value.value)));

    if (facet.name === 'availability') {
      const groupedCounts = values.reduce(
        (accumulator, value) => {
          const [from] = this.parseRangeValue(value.value);
          const normalizedValue = from >= 1 ? '1' : '0';

          accumulator[normalizedValue] += Number(value.hits_count || 0);
          return accumulator;
        },
        { '0': 0, '1': 0 }
      );

      return [
        {
          key: '1',
          label: 'Skladem',
          hitsCount: groupedCounts['1'],
          serializedValue: this.serializeFilterValues(['1']),
          checked: activeValues.includes('1'),
        },
        {
          key: '0',
          label: 'Není skladem',
          hitsCount: groupedCounts['0'],
          serializedValue: this.serializeFilterValues(['0']),
          checked: activeValues.includes('0'),
        },
      ].filter((entry) => entry.hitsCount > 0 || entry.checked);
    }

    return values.map((value) => {
      const rawValue = String(value.value);

      return {
        key: rawValue,
        label: this.getFacetValueLabel(facet, rawValue),
        hitsCount: Number(value.hits_count || 0),
        serializedValue: this.serializeFilterValues([rawValue]),
        checked: activeValues.includes(rawValue),
      };
    });
  }

  buildFacetMarkup(facet, activeFilters, index, facetOpenState = {}) {
    const hasSavedState = Object.prototype.hasOwnProperty.call(facetOpenState, facet.name);
    const isOpen = hasSavedState ? (facetOpenState[facet.name] ? ' open' : '') : (index < 3 ? ' open' : '');
    const entries = this.getFacetEntries(facet, activeFilters);

    if (!entries.length) {
      return '';
    }

    const facetLabel = this.getFacetDisplayName(facet.name);
    const selectedEntriesCount = entries.filter((entry) => entry.checked).length;
    const valuesMarkup = entries
      .map((entry, valueIndex) => {
        const inputId = `LuigisboxFacet-${facet.name}-${valueIndex + 1}`;
        const labelClass = `facets__label facet-checkbox${entry.checked ? ' active' : ''}`;

        return `
          <li class="list-menu__item facets__item">
            <label for="${this.escapeHtml(inputId)}" class="${this.escapeHtml(labelClass)}">
                <input
                  id="${this.escapeHtml(inputId)}"
                  type="checkbox"
                  name="f[]"
                  data-facet-name="${this.escapeHtml(facet.name)}"
                  value="${this.escapeHtml(entry.serializedValue)}"
                  ${entry.checked ? 'checked' : ''}
                >
                ${this.getCheckboxIconMarkup()}
                <span class="facet-checkbox__text" aria-hidden="true">
                  <span class="facet-checkbox__text-label">${this.escapeHtml(entry.label)}</span> (${this.escapeHtml(String(entry.hitsCount))})
                </span>
                <span class="visually-hidden">${this.escapeHtml(`${entry.label} (${entry.hitsCount})`)}</span>
            </label>
          </li>
        `;
      })
      .join('');

    return `
      <details
        id="Details-${this.escapeHtml(facet.name)}-Luigisbox"
        class="luigisbox-search-page__facet facets__disclosure-vertical js-filter"
        data-facet-name="${this.escapeHtml(facet.name)}"${isOpen}
      >
        <summary
          class="facets__summary caption-large focus-offset"
          aria-label="${this.escapeHtml(`${facetLabel} (${selectedEntriesCount})`)}"
        >
          <div>
            <span class="facets__summary-label">
              ${this.escapeHtml(facetLabel)}
              <span class="facets__selected${selectedEntriesCount === 0 ? ' hidden' : ''}">(${this.escapeHtml(String(selectedEntriesCount))})</span>
            </span>
            ${this.getCaretIconMarkup()}
          </div>
        </summary>
        <div id="Facet-${index + 1}-Luigisbox" class="parent-display facets__display-vertical">
          <fieldset class="facets-wrap parent-wrap facets-wrap-vertical">
            <legend class="visually-hidden">${this.escapeHtml(facetLabel)}</legend>
            <ul class="facets-layout facets-layout-list facets-layout-list--text facets__list--vertical list-unstyled" role="list">
              ${valuesMarkup}
            </ul>
          </fieldset>
        </div>
      </details>
    `;
  }

  renderLuigisboxResults(hits, productCards, query) {
    if (!hits.length) {
      this.resultsContainer.innerHTML = `
        <div class="title-wrapper center luigisbox-search-page__placeholder">
          <h2 class="title title--primary">${this.escapeHtml(this.emptyResultsText)}</h2>
          <p>${this.escapeHtml(`Dotaz: ${query}`)}</p>
        </div>
      `;
      return;
    }

    const cardsByUrl = productCards.reduce((accumulator, item) => {
      accumulator[item.url] = item.cardMarkup;
      return accumulator;
    }, {});

    const itemsMarkup = hits
      .map((hit, index) => {
        const cardMarkup = cardsByUrl[hit.url];

        if (cardMarkup) {
          return `
            <div class="luigisbox-search-page__product-card" data-index="${index + 1}">
              ${cardMarkup}
            </div>
          `;
        }

        /*return `
          <div class="luigisbox-search-page__product-card" data-index="${index + 1}">
            <a href="${this.escapeHtml(hit.url)}" class="predictive-search__item link link--text">
              <div class="predictive-search__item-content predictive-search__item-content--centered">
                <p class="predictive-search__item-heading h5">${this.escapeHtml(this.getHitTitle(hit))}</p>
              </div>
            </a>
          </div>
        `;*/
        return "";
      })
      .join('');

    this.resultsContainer.innerHTML = `
      <div class="quick-order-list-container luigisbox-search-page__results-shell">
        <div
          class="collection-quick-add-bulk luigisbox-search-page__results-list"
          data-id="${this.escapeHtml(this.productGrid?.dataset.id || 'main-search')}"
          data-section="${this.escapeHtml(this.productGrid?.dataset.id || 'main-search')}"
          data-product-id="luigisbox-search-page"
          data-url="${this.escapeHtml(window.location.pathname)}"
        >
          <form action="${routes.cart_update_url}" class="quick-order-list__contents critical-hidden" method="post">
            <div class="quick-order-list__container">
              <div class="js-contents">
                <div class="quick-order-list__table wishlist-table">
                  ${itemsMarkup}
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    `;
  }

  renderLuigisboxPagination(totalHits, state) {
    if (!this.paginationContainer) return;

    const totalPages = Math.ceil(totalHits / this.luigisboxPageSize);

    if (totalPages <= 1 || !state.query) {
      this.paginationContainer.innerHTML = '';
      return;
    }

    const parts = this.buildPaginationParts(totalPages, state.page);
    const itemsMarkup = parts
      .map((part) => {
        if (part.type === 'ellipsis') {
          return '<li><span class="pagination__item">…</span></li>';
        }

        if (part.type === 'current') {
          return `
            <li>
              <span class="pagination__item pagination__item--current light" aria-current="page">${part.page}</span>
            </li>
          `;
        }

        const label = part.type === 'previous' ? 'Předchozí' : part.type === 'next' ? 'Další' : `Strana ${part.page}`;
        const extraClass =
          part.type === 'previous'
            ? ' pagination__item--next pagination__item-arrow'
            : part.type === 'next'
              ? ' pagination__item--prev pagination__item-arrow'
              : '';

        return `
          <li>
            <a href="${this.escapeHtml(this.buildUrlForState({ ...state, page: part.page }).toString())}" class="pagination__item link${extraClass}" data-page="${part.page}" aria-label="${this.escapeHtml(label)}">
              ${part.type === 'previous' ? '&lsaquo;' : part.type === 'next' ? '&rsaquo;' : part.page}
            </a>
          </li>
        `;
      })
      .join('');

    this.paginationContainer.innerHTML = `
      <div class="pagination-wrapper">
        <nav class="pagination" role="navigation" aria-label="Stránkování výsledků">
          <ul class="pagination__list list-unstyled" role="list">
            ${itemsMarkup}
          </ul>
        </nav>
      </div>
    `;
  }

  buildPaginationParts(totalPages, currentPage) {
    const pages = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const safePages = Array.from(pages).filter((page) => page >= 1 && page <= totalPages).sort((a, b) => a - b);
    const parts = [];

    if (currentPage > 1) {
      parts.push({ type: 'previous', page: currentPage - 1 });
    }

    safePages.forEach((page, index) => {
      const previousPage = safePages[index - 1];

      if (previousPage && page - previousPage > 1) {
        parts.push({ type: 'ellipsis' });
      }

      parts.push({
        type: page === currentPage ? 'current' : 'page',
        page,
      });
    });

    if (currentPage < totalPages) {
      parts.push({ type: 'next', page: currentPage + 1 });
    }

    return parts;
  }

  setLuigisboxLoadingState(isLoading) {
    if (this.loadingOverlay) {
      this.loadingOverlay.hidden = !isLoading;
      this.loadingOverlay.classList.toggle('is-active', isLoading);
    }
  }

  scrollLuigisboxResultsIntoView() {
    const resultsTop = this.resultsContainer?.getBoundingClientRect().top;

    if (typeof resultsTop !== 'number') return;

    window.scrollTo({
      top: window.scrollY + resultsTop - 120,
      behavior: 'smooth',
    });
  }

  getFacetDisplayName(facetName) {
    switch (facetName) {
      case 'brand':
        return 'Značka';
      case 'Původní prodejce':
        return 'Původní prodejce';
      case 'Leaf collection name':
        return 'Kategorie';
      case 'availability':
        return 'Dostupnost';
      case 'Label':
        return 'Štítky';
      case 'Klíčové atributy_json.atributy':
        return 'Vlastnosti produktu';
      default:
        return facetName
          .split(/[_-]/)
          .filter(Boolean)
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' ');
    }
  }

  getFacetValueLabel(facet, rawValue) {
    if (facet.name === 'availability') {
      if (String(rawValue) === '1') return 'Skladem';
      if (String(rawValue) === '0') return 'Není skladem';

      const [from, to] = this.parseRangeValue(rawValue);

      if (to <= 1) return 'Není skladem';
      if (from >= 1) return 'Skladem';
    }

    return String(rawValue);
  }

  getFallbackFacetValueLabel(facetName, rawValue) {
    if (facetName === 'availability') {
      if (String(rawValue) === '1') return 'Skladem';
      if (String(rawValue) === '0') return 'Není skladem';
    }

    return String(rawValue);
  }

  getFacetOpenState() {
    if (!this.filtersContainer) {
      return {};
    }

    return Array.from(this.filtersContainer.querySelectorAll('details[data-facet-name]')).reduce((state, element) => {
      state[element.dataset.facetName] = element.hasAttribute('open');
      return state;
    }, {});
  }

  getCheckboxIconMarkup() {
    return `
      <svg
        width="1.6rem"
        height="1.6rem"
        viewBox="0 0 16 16"
        aria-hidden="true"
        focusable="false"
      >
        <rect width="16" height="16" stroke="currentColor" fill="none" stroke-width="1"></rect>
      </svg>

      <svg
        aria-hidden="true"
        class="icon icon-checkmark"
        width="1.1rem"
        height="0.7rem"
        viewBox="0 0 11 7"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M1.5 3.5L2.83333 4.75L4.16667 6L9.5 1"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    `;
  }

  getCloseSmallIconMarkup() {
    return `
      <svg
        aria-hidden="true"
        focusable="false"
        width="12"
        height="13"
        class="icon icon-close-small"
        viewBox="0 0 12 13"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M8.48627 9.32917L2.82849 3.67098" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
        <path d="M2.88539 9.38504L8.42932 3.61524" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" />
      </svg>
    `;
  }

  getCaretIconMarkup() {
    return `
      <svg aria-hidden="true" focusable="false" class="icon icon-caret" viewBox="0 0 10 6">
        <path
          fill-rule="evenodd"
          clip-rule="evenodd"
          d="M9.354.646a.5.5 0 00-.708 0L5 4.293 1.354.646a.5.5 0 00-.708.708l4 4a.5.5 0 00.708 0l4-4a.5.5 0 000-.708z"
          fill="currentColor"
        ></path>
      </svg>
    `;
  }

  parseRangeValue(value) {
    const parts = String(value)
      .split('|')
      .map((part) => parseFloat(part));
    const from = parts[0];
    const to = parts.length > 1 ? parts[1] : parts[0];

    return [Number.isFinite(from) ? from : 0, Number.isFinite(to) ? to : 0];
  }

  serializeFilterValues(values) {
    return values.join('||');
  }

  deserializeFilterValues(value) {
    return String(value || '')
      .split('||')
      .map((part) => part.trim())
      .filter(Boolean);
  }

  formatMoney(value) {
    return new Intl.NumberFormat('cs-CZ', {
      style: 'currency',
      currency: this.currencyCode,
      maximumFractionDigits: 2,
    }).format(value);
  }

  getHitTitle(hit) {
    const rawTitle = hit?.attributes?.title;
    const normalizedTitle = Array.isArray(rawTitle) ? rawTitle[0] : rawTitle;
    return this.stripHtml(normalizedTitle || hit?.url || '');
  }

  stripHtml(value) {
    const element = document.createElement('div');
    element.innerHTML = String(value || '');
    return element.textContent || element.innerText || '';
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('main-search', MainSearch);
